import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ChevronLeft, MapPin, Clock, Users, Star, Tag, Check, MessageCircle, Loader2,
} from 'lucide-react';
import { photoSrc } from '../utils/photo';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1620694655075-6ed740167a66?auto=format&fit=crop&q=80&w=800';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { format: formatPrice, currency: displayCurrency } = useCurrency();
  const { user, api } = useAuth();
  const [product, setProduct] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [buying, setBuying] = useState(false);
  const [form, setForm] = useState({ people_count: 1, date: '', time: '', notes: '' });

  useEffect(() => {
    axios.get(`${API_URL}/api/products/${productId}`)
      .then(r => setProduct(r.data))
      .catch(() => toast.error(language === 'en' ? 'Product not found' : 'Producto no encontrado'));
  }, [productId, language]);

  const handleBuy = async () => {
    if (!user) { navigate(`/auth?next=/product/${productId}`); return; }
    setBuying(true);
    try {
      const { data } = await api().post(`/products/${productId}/buy`, {
        product_id: productId,
        people_count: parseInt(form.people_count) || 1,
        date: form.date || null,
        time: form.time || null,
        notes: form.notes || null,
      });
      toast.success(language === 'en' ? 'Chat opened with provider!' : '¡Chat abierto con el proveedor!');
      navigate(`/dashboard/bookings/${data.id}`);
    } catch (e) {
      const msg = e.response?.data?.detail || (language === 'en' ? 'Error' : 'Error');
      toast.error(msg);
    } finally {
      setBuying(false);
    }
  };

  if (!product) {
    return (
      <MobileShell>
        <TopBar showBrand={false} />
        <div className="p-8 text-center text-muted-foreground">
          <Loader2 className="mx-auto animate-spin" />
        </div>
      </MobileShell>
    );
  }

  const photos = product.photos?.length ? product.photos.map((p) => photoSrc(p)).filter(Boolean) : [FALLBACK_IMAGE];
  const productCurrency = (product.currency || 'USD').toUpperCase();
  const pricingMode = product.pricing_mode === 'flat' ? 'flat' : 'per_person';
  const unitPrice = product.final_price || product.price;
  const peopleCount = parseInt(form.people_count) || 1;
  const totalPrice = pricingMode === 'flat' ? unitPrice : unitPrice * peopleCount;
  const reservationFee = +(totalPrice * 0.17 * 1.16).toFixed(2);
  const balanceToProvider = +(totalPrice - reservationFee).toFixed(2);

  // Localised "includes" list — fall back to legacy single-list field if English is empty.
  const includesList = (language === 'en' && product.includes_en?.length)
    ? product.includes_en
    : (product.includes || []);

  // 12h advance check (UI only — backend also enforces)
  const meetsAdvanceNotice = (() => {
    if (!form.date) return true; // we'll allow null date and let user know
    try {
      const dt = new Date(`${form.date}T${form.time || '12:00'}:00`);
      return dt.getTime() - Date.now() >= 12 * 3600 * 1000;
    } catch { return true; }
  })();

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={product.title}
        right={
          <Link to="/catalog" className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ChevronLeft size={18} />
          </Link>
        }
      />

      {/* Photo carousel */}
      <div className="relative aspect-square bg-muted">
        <img
          src={photos[photoIdx]}
          alt={product.title}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="product-main-photo"
        />
        {product.discount_percent > 0 && (
          <span className="absolute top-3 left-3 bg-rosa text-white text-xs font-bold rounded-full px-3 py-1 flex items-center gap-1 shadow-lg">
            <Tag size={12} /> -{product.discount_percent}% OFF
          </span>
        )}
        {photos.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={() => setPhotoIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === photoIdx ? 'bg-card w-6' : 'bg-white/50 w-1.5'}`}
              />
            ))}
          </div>
        )}
      </div>
      {photos.length > 1 && (
        <div className="flex gap-2 px-4 -mt-8 relative z-10 overflow-x-auto scrollbar-hide pb-2">
          {photos.map((p, i) => (
            <button
              key={i}
              onClick={() => setPhotoIdx(i)}
              className={`shrink-0 w-14 h-14 rounded-xl overflow-hidden border-2 ${i === photoIdx ? 'border-caribbean' : 'border-white/50'}`}
            >
              <img src={p} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-4">
        <div>
          <p className="text-xs text-caribbean font-semibold uppercase tracking-wide">
            {product.category}
          </p>
          <h1 className="font-heading text-2xl font-bold leading-tight mt-1">{product.title}</h1>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1.5">
            {product.location && (
              <span className="inline-flex items-center gap-1"><MapPin size={12} /> {product.location.replaceAll('_', ' ')}</span>
            )}
            {product.duration && (
              <span className="inline-flex items-center gap-1"><Clock size={12} /> {product.duration}</span>
            )}
            {product.max_people && (
              <span className="inline-flex items-center gap-1"><Users size={12} /> {product.max_people}</span>
            )}
          </div>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-heading text-3xl font-bold text-caribbean">
            {formatPrice(product.final_price, productCurrency, { withCode: false })}
          </span>
          {product.discount_percent > 0 && (
            <span className="text-base text-muted-foreground line-through">
              {formatPrice(product.price, productCurrency, { withCode: false })}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            {displayCurrency} / {pricingMode === 'flat'
              ? (language === 'en' ? 'group' : 'grupo')
              : (language === 'en' ? 'person' : 'persona')}
          </span>
          {displayCurrency !== productCurrency && (
            <span className="text-[10px] text-muted-foreground italic w-full">
              {language === 'en'
                ? `Charged in ${productCurrency} by the provider`
                : `Cobrado en ${productCurrency} por el proveedor`}
            </span>
          )}
        </div>

        {/* Provider */}
        {product.provider && (
          <Link
            to={`/provider/${product.provider.id}`}
            className="flex items-center gap-3 bg-muted/50 rounded-2xl p-3"
            data-testid="product-provider-link"
          >
            {product.provider.profile_photo ? (
              <img src={`${API_URL}/api/files/${product.provider.profile_photo}`} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-caribbean/20 flex items-center justify-center text-caribbean font-bold">
                {product.provider.name?.[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{product.provider.company_name || product.provider.name}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Star size={11} className="text-gold fill-gold" /> {product.provider.rating?.toFixed(1) || '5.0'}
                {product.provider.is_verified && <Check size={11} className="text-caribbean ml-1" />}
              </p>
            </div>
            <ChevronLeft className="rotate-180 text-muted-foreground" size={16} />
          </Link>
        )}

        {/* Description */}
        <div>
          <h3 className="font-heading font-bold text-sm mb-1">
            {language === 'en' ? 'About' : 'Acerca de'}
          </h3>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {language === 'es' ? product.description_es : product.description_en}
          </p>
        </div>

        {/* Includes */}
        {includesList.length > 0 && (
          <div>
            <h3 className="font-heading font-bold text-sm mb-2">
              {language === 'en' ? "What's included" : 'Qué incluye'}
            </h3>
            <ul className="space-y-1.5">
              {includesList.map((inc, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <Check size={14} className="text-caribbean shrink-0" /> {inc}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Booking form */}
        <div className="bg-muted/50 rounded-2xl p-4 space-y-3">
          <h3 className="font-heading font-bold text-sm">
            {language === 'en' ? 'Reserve & chat' : 'Reservar y chatear'}
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">{language === 'en' ? 'People' : 'Personas'}</Label>
              <Input
                data-testid="product-people-input"
                type="number" min="1"
                value={form.people_count}
                onChange={(e) => setForm({ ...form, people_count: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">{language === 'en' ? 'Date' : 'Fecha'}</Label>
              <Input
                data-testid="product-date-input"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">{language === 'en' ? 'Time (optional)' : 'Hora (opcional)'}</Label>
            <Input
              data-testid="product-time-input"
              type="time"
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
            />
          </div>

          <div className="border-t border-border pt-3 space-y-1.5">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-muted-foreground">{language === 'en' ? 'Experience total' : 'Total experiencia'}</span>
              <span className="font-semibold">{formatPrice(totalPrice, productCurrency)}</span>
            </div>
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-muted-foreground">
                {language === 'en' ? 'Reservation now (17% + IVA)' : 'Reservar ahora (17% + IVA)'}
              </span>
              <span className="font-heading text-lg font-bold text-caribbean">
                {formatPrice(reservationFee, productCurrency, { withCode: false })}
              </span>
            </div>
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-muted-foreground">
                {language === 'en' ? 'Pay to provider on arrival' : 'Pagas al proveedor al llegar'}
              </span>
              <span className="font-semibold text-rosa">{formatPrice(balanceToProvider, productCurrency)}</span>
            </div>
            <p className="text-[10px] text-muted-foreground pt-1">
              {language === 'en'
                ? `You will be charged in ${productCurrency} via Stripe. Equivalent shown for reference.`
                : `Stripe te cobrará en ${productCurrency}. Equivalente mostrado como referencia.`}
            </p>
          </div>

          {!meetsAdvanceNotice && form.date && (
            <p className="text-[11px] text-rosa bg-rosa/10 rounded-lg px-3 py-2">
              {language === 'en'
                ? '⚠ Catalog bookings need 12h notice. For sooner, create a custom search.'
                : '⚠ Las reservas de catálogo requieren 12h de anticipación. Para algo más cercano, crea una búsqueda.'}
            </p>
          )}

          <Button
            data-testid="product-buy-btn"
            onClick={handleBuy}
            disabled={buying || !meetsAdvanceNotice}
            className="w-full h-12 rounded-2xl bg-rosa hover:bg-rosa/90 text-white font-semibold disabled:opacity-50"
          >
            {buying ? <Loader2 className="animate-spin" /> : (
              <>
                <MessageCircle className="mr-2" size={18} />
                {language === 'en'
                  ? `Reserve for ${formatPrice(reservationFee, productCurrency, { withCode: false })}`
                  : `Reservar por ${formatPrice(reservationFee, productCurrency, { withCode: false })}`}
              </>
            )}
          </Button>
          <p className="text-[10px] text-center text-muted-foreground">
            {language === 'en'
              ? 'You only pay the reservation fee now. A chat with the provider opens to coordinate.'
              : 'Solo pagas la reserva ahora. Se abre un chat con el proveedor para coordinar.'}
          </p>
        </div>
      </div>
    </MobileShell>
  );
}
